import { useState, useEffect, useCallback, KeyboardEvent } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Leaf, Calendar, Truck, Sprout, Instagram, Mail, MapPin, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { OptimizedImage, OptimizedBackgroundImage } from "@/components/ui/optimized-image";
// Cookie consent disabled - not required outside EU
// import { CookieSettingsButton } from "@/components/CookieConsentBanner";
import { NewsletterSignup } from "@/components/newsletter";

// Keyboard navigation helper for clickable cards
function useKeyboardClick(onClick: () => void) {
  return useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  }, [onClick]);
}

import heroImage from "@assets/generated_images/lush_terrarium_hero_image.png";
import desertTerrarium from "@assets/generated_images/desert_terrarium_product.png";
import mossTerrarium from "@assets/generated_images/moss_forest_terrarium.png";
import tropicalTerrarium from "@assets/generated_images/geometric_tropical_terrarium.png";
import ryanImage from "@assets/generated_images/ryan_the_artisan_maker.png";
import craftingImage from "@assets/generated_images/terrarium_crafting_process.png";

const galleryImages = [
  { src: mossTerrarium, alt: "Forest Oasis Terrarium", name: "Forest Oasis" },
  { src: desertTerrarium, alt: "DesertScape Terrarium", name: "DesertScape" },
  { src: tropicalTerrarium, alt: "Tropical Paradise Terrarium", name: "Tropical Paradise" },
  { src: mossTerrarium, alt: "Custom Deluxe Terrarium", name: "Deluxe Custom" },
];

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      role="banner"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
        <a
          href="/"
          className="font-serif text-2xl font-semibold tracking-tight"
          data-testid="link-home"
          aria-label="Life Itself Studio - Go to homepage"
        >
          Life Itself Studio
        </a>

        <nav className="hidden md:flex items-center gap-8" role="navigation" aria-label="Main menu">
          <button
            onClick={() => scrollToSection("gallery")}
            className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
            data-testid="link-gallery"
            aria-label="Navigate to gallery section"
          >
            Gallery
          </button>
          <button
            onClick={() => setLocation("/gallery")}
            className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
            data-testid="link-showcase"
            aria-label="View customer showcase gallery"
          >
            Showcase
          </button>
          <button
            onClick={() => scrollToSection("process")}
            className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
            data-testid="link-process"
            aria-label="Navigate to our process section"
          >
            Our Process
          </button>
          <button
            onClick={() => setLocation("/checkout")}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-emerald-900/60 backdrop-blur-sm text-emerald-100 border border-emerald-400 shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-800/70 hover:border-emerald-300"
            data-testid="button-preorder-nav"
            aria-label="Start designing your custom terrarium"
          >
            <Sprout className="w-4 h-4" aria-hidden="true" />
            Design Your Terrarium
          </button>
        </nav>

        <button
          onClick={() => setLocation("/checkout")}
          className="md:hidden flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-emerald-900/60 backdrop-blur-sm text-emerald-100 border border-emerald-400 shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-800/70 hover:border-emerald-300"
          data-testid="button-design-terrarium-mobile"
          aria-label="Start designing your custom terrarium"
        >
          <Sprout className="w-4 h-4" aria-hidden="true" />
          Design Your Terrarium
        </button>
      </div>
    </header>
  );
}

function Hero() {
  const [, setLocation] = useLocation();

  const handlePreOrderClick = () => {
    setLocation("/checkout");
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-labelledby="hero-heading"
    >
      <OptimizedBackgroundImage
        src={heroImage}
        lazy={false}
        className="absolute inset-0"
        backgroundSize="cover"
        backgroundPosition="center"
        aria-hidden="true"
      >
        <div className="absolute inset-0" />
      </OptimizedBackgroundImage>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" aria-hidden="true" />

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.h1
          id="hero-heading"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-serif text-5xl md:text-7xl font-semibold text-white mb-6 leading-tight"
        >
          Living Art for<br />Your Space
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto"
        >
          Handcrafted terrariums that bring the beauty of nature into your home.
          Each piece is a unique miniature world, lovingly created in Colorado.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Button
            size="lg"
            onClick={handlePreOrderClick}
            className="bg-white/20 backdrop-blur-md border border-white/30 text-white px-8 py-6 text-lg"
            data-testid="button-preorder-hero"
            aria-label="Start designing your custom terrarium"
          >
            Design Your Terrarium
          </Button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <ChevronDown className="w-8 h-8 text-white/60 animate-bounce" />
      </motion.div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: Leaf,
      title: "Choose Your Style",
      description: "Select from our curated collection of terrarium styles, from lush forest oases to serene desert landscapes.",
    },
    {
      icon: Calendar,
      title: "Pick Your Delivery Week",
      description: "Choose a delivery week that works for you. We build each terrarium fresh, just for you.",
    },
    {
      icon: Truck,
      title: "We Build & Deliver",
      description: "Ryan handcrafts your terrarium with care, then delivers it right to your door in the Denver area.",
    },
  ];

  return (
    <section className="py-24 bg-muted/30" aria-labelledby="how-it-works-heading">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2
            id="how-it-works-heading"
            className="font-serif text-3xl md:text-4xl font-medium mb-4"
            data-testid="text-how-it-works-title"
          >
            How It Works
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Getting your own handcrafted terrarium is simple. We've streamlined the process
            so you can focus on enjoying your new living art piece.
          </p>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 list-none" role="list" aria-label="Steps to order your terrarium">
          {steps.map((step, index) => (
            <motion.li
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
              aria-label={`Step ${index + 1}: ${step.title}`}
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center" aria-hidden="true">
                <step.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-serif text-xl font-medium mb-3" data-testid={`text-step-${index + 1}-title`}>
                {step.title}
              </h3>
              <p className="text-muted-foreground" data-testid={`text-step-${index + 1}-desc`}>
                {step.description}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const offerings = [
  {
    id: "desert",
    title: "Desert Biome",
    subtitle: "Serene & Low Maintenance",
    description: "Succulents, cacti, and beautiful sand formations in a stunning glass vessel",
    image: desertTerrarium,
    features: ["Drought-tolerant plants", "Natural sand & stones", "Minimal watering needed"],
    timeline: "Built fresh in 4-6 weeks",
    icon: "sun",
  },
  {
    id: "forest",
    title: "Forest Biome",
    subtitle: "Lush & Vibrant",
    description: "Miniature woodland with moss, ferns, and shade-loving plants",
    image: mossTerrarium,
    features: ["Self-sustaining ecosystem", "Living moss carpet", "Tiny ferns & foliage"],
    timeline: "Built fresh in 4-6 weeks",
    icon: "tree",
  },
  {
    id: "premium",
    title: "Premium Plants",
    subtitle: "Rare & Exotic Upgrade",
    description: "Elevate your terrarium with hand-selected rare plant varieties",
    image: tropicalTerrarium,
    features: ["Exotic species", "Collector's varieties", "Unique specimens"],
    timeline: "Sourced specially for you",
    icon: "sparkle",
  },
  {
    id: "custom",
    title: "Custom Design",
    subtitle: "1-on-1 with Ryan",
    description: "Personal consultation to design your perfect living world",
    image: craftingImage,
    features: ["15-min consultation", "Your vision realized", "Bespoke creation"],
    timeline: "Design call before build",
    icon: "message",
  },
  {
    id: "experience",
    title: "Nature Experience",
    subtitle: "Full-Day Adventure",
    description: "Hike Colorado wilderness, gather materials, and build with Ryan",
    image: heroImage,
    features: ["Morning nature hike", "Material foraging", "Workshop with Ryan"],
    timeline: "Full day, 2-4 guests",
    icon: "mountain",
  },
];

function Gallery() {
  const [, setLocation] = useLocation();

  return (
    <section id="gallery" className="py-24" aria-labelledby="gallery-heading">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 id="gallery-heading" className="font-serif text-3xl md:text-4xl font-medium mb-4" data-testid="text-gallery-title">
            Our Offerings
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            From self-contained ecosystems to immersive nature experiences.
            Discover what speaks to you.
          </p>
        </div>

        {/* Main Biomes - Large Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" role="list" aria-label="Terrarium offerings">
          {offerings.slice(0, 2).map((offering, index) => (
            <motion.article
              key={offering.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={() => setLocation("/checkout")}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLocation("/checkout"); } }}
              tabIndex={0}
              role="listitem"
              aria-label={`${offering.title}: ${offering.description}. ${offering.timeline}`}
              data-testid={`card-offering-${offering.id}`}
            >
              <OptimizedImage
                src={offering.image}
                alt={offering.title}
                fill
                objectFit="cover"
                className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105"
                priority={index < 2}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 z-10" />

              {/* Content Overlay */}
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-white z-20">
                <div className="mb-2">
                  <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium mb-3">
                    {offering.subtitle}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-semibold mb-2">{offering.title}</h3>
                <p className="text-white/80 text-sm md:text-base mb-4 max-w-md">{offering.description}</p>

                {/* Features */}
                <div className="space-y-1 mb-4">
                  {offering.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-white/70">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{offering.timeline}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Upgrades & Experiences - Smaller Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" role="list" aria-label="Additional offerings and upgrades">
          {offerings.slice(2).map((offering, index) => (
            <motion.article
              key={offering.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: (index + 2) * 0.1 }}
              viewport={{ once: true }}
              className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={() => setLocation("/checkout")}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLocation("/checkout"); } }}
              tabIndex={0}
              role="listitem"
              aria-label={`${offering.title}: ${offering.description}. ${offering.timeline}`}
              data-testid={`card-offering-${offering.id}`}
            >
              <OptimizedImage
                src={offering.image}
                alt={offering.title}
                fill
                objectFit="cover"
                className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105"
                rootMargin="300px"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 z-10" />

              {/* Content Overlay */}
              <div className="absolute inset-0 p-5 flex flex-col justify-end text-white z-20">
                <span className="inline-block self-start px-2.5 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium mb-2">
                  {offering.subtitle}
                </span>
                <h3 className="font-serif text-xl font-semibold mb-1.5">{offering.title}</h3>
                <p className="text-white/70 text-sm mb-3 line-clamp-2">{offering.description}</p>

                {/* Features - Compact */}
                <div className="space-y-0.5 mb-3">
                  {offering.features.slice(0, 2).map((feature, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-white/60">
                      <div className="w-1 h-1 rounded-full bg-primary" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div className="flex items-center gap-1.5 text-xs text-white/50">
                  <Calendar className="w-3 h-3" />
                  <span>{offering.timeline}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const [, setLocation] = useLocation();

  return (
    <section id="process" className="py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-medium" data-testid="text-process-title">
              Crafted with Care,<br />Delivered with Love
            </h2>

            <p className="text-muted-foreground text-lg">
              Hi, I'm Ryan. After years of creating terrariums as a passion project,
              I decided to turn my love for miniature ecosystems into something I could share with others.
            </p>

            <p className="text-muted-foreground">
              Each terrarium is built by hand in my studio in Colorado. I source materials
              from local suppliers and the beautiful Colorado wilderness, ensuring every piece
              has a unique character that can't be replicated.
            </p>

            <p className="text-muted-foreground">
              From the careful selection of plants to the artistic arrangement of stones and
              driftwood, every element is thoughtfully placed to create a thriving miniature world
              that will bring nature into your space for years to come.
            </p>

            <div className="pt-4">
              <Button
                onClick={() => setLocation("/checkout")}
                data-testid="button-preorder-process"
              >
                Design Your Terrarium
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-4"
          >
            <div className="rounded-lg overflow-hidden h-64">
              <OptimizedImage
                src={ryanImage}
                alt="Ryan crafting a terrarium"
                fill
                objectFit="cover"
                rootMargin="400px"
                data-testid="img-ryan"
              />
            </div>
            <div className="rounded-lg overflow-hidden h-48">
              <OptimizedImage
                src={craftingImage}
                alt="Terrarium crafting process"
                fill
                objectFit="cover"
                rootMargin="400px"
                data-testid="img-crafting"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CallToAction() {
  const [, setLocation] = useLocation();

  return (
    <section id="preorder" className="py-24 bg-muted/30">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4" data-testid="text-preorder-title">
            Ready for Your Own Living World?
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
            Handcrafted terrariums delivered to your door in Colorado. Each piece is a unique work of art.
          </p>
          <Button
            size="lg"
            onClick={() => setLocation("/checkout")}
            className="px-12 py-6 text-lg"
            data-testid="button-start-order"
          >
            Design Your Terrarium
          </Button>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  const [, setLocation] = useLocation();

  return (
    <footer role="contentinfo" className="py-16 border-t border-border">
      <div className="max-w-7xl mx-auto px-4">
        {/* Newsletter Section */}
        <section aria-labelledby="footer-newsletter" className="mb-12 pb-12 border-b border-border">
          <div className="max-w-xl mx-auto text-center">
            <h3 id="footer-newsletter" className="font-serif text-xl font-semibold mb-2">
              Join Our Newsletter
            </h3>
            <p className="text-muted-foreground mb-6">
              Get terrarium care tips, exclusive offers, and first access to new designs.
            </p>
            <NewsletterSignup
              source="footer"
              variant="inline"
              className="max-w-md mx-auto"
            />
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <section aria-labelledby="footer-brand">
            <h3 id="footer-brand" className="font-serif text-xl font-semibold mb-4">
              Life Itself Studio
            </h3>
            <p className="text-muted-foreground">
              Handcrafted terrariums bringing nature's beauty into your home.
              Made with love in Colorado.
            </p>
          </section>

          <section aria-labelledby="footer-contact">
            <h4 id="footer-contact" className="font-medium mb-4">Contact</h4>
            <address className="space-y-3 text-muted-foreground not-italic">
              <a
                href="mailto:hello@lifeitself.studio"
                className="flex items-center gap-2 hover:text-foreground transition-colors"
                data-testid="link-email"
                aria-label="Send email to hello@lifeitself.studio"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                hello@lifeitself.studio
              </a>
              <a
                href="https://instagram.com/lifeitself.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-foreground transition-colors"
                data-testid="link-instagram"
                aria-label="Follow Life Itself Studio on Instagram (opens in new tab)"
              >
                <Instagram className="w-4 h-4" aria-hidden="true" />
                @lifeitself.studio
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" aria-hidden="true" />
                <span>Denver, Colorado</span>
              </div>
            </address>
          </section>

          <nav aria-labelledby="footer-links">
            <h4 id="footer-links" className="font-medium mb-4">Quick Links</h4>
            <div className="space-y-2">
              <button
                onClick={() => document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" })}
                className="block text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-footer-gallery"
                aria-label="Navigate to gallery section"
              >
                Gallery
              </button>
              <button
                onClick={() => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" })}
                className="block text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-footer-process"
                aria-label="Navigate to our process section"
              >
                Our Process
              </button>
              <button
                onClick={() => setLocation("/checkout")}
                className="block text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-footer-preorder"
                aria-label="Start designing your terrarium"
              >
                Design Your Terrarium
              </button>
              <button
                onClick={() => setLocation("/privacy")}
                className="block text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-footer-privacy"
                aria-label="View privacy policy"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => setLocation("/email-preferences")}
                className="block text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-footer-email-preferences"
                aria-label="Manage email preferences"
              >
                Email Preferences
              </button>
            </div>
          </nav>
        </div>

        <div className="mt-12 pt-8 border-t border-border text-center text-muted-foreground text-sm">
          <p data-testid="text-copyright">
            &copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Skip to main content link for keyboard/screen reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      <Header />
      <main id="main-content" tabIndex={-1}>
        <Hero />
        <HowItWorks />
        <Gallery />
        <Process />
        <CallToAction />
      </main>
      <Footer />
    </div>
  );
}
